import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2290DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 2290 Deadlines: Important Dates and Filing Schedule"
          description="Stay informed about Form 2290 due dates and filing schedule. Don't miss the deadlines for Heavy Vehicle Use Tax. Get the details you need to stay compliant."
          keywords="form 2290 due date, 2290 form due date, form 2290 due date 2026, form 2290 due date 2026-2027, irs form 2290 due date"
          canonical="resources/excise-tax/form-2290-due-dates/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          Form 2290{' '}
          <span className="highlight-container">
            <span className="highlight">Due Dates</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File your 2026-2027 Form 2290 (HVUT) by{' '}
          <span style={{ fontWeight: 600 }}>August 31, 2026!</span> E-file with
          TaxZerone <br className="hide-xs" />
          and get your{' '}
          <span style={{ fontWeight: 600 }}>IRS Schedule 1 in minutes</span>.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290duedates`}
          >
            E-file Form 2290
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/excise-tax/form-2290-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-21T09:00:00-05:00"
                      >
                        August 21, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-21T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        August 21, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <Link
                          itemProp="url"
                          href="https://www.taxzerone.com/"
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </Link>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 2290 Due Dates at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Due Date:</strong>{' '}
                        Form 2290 must be filed by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          last day of the month following the vehicle's First
                          Used Month (FUM){' '}
                        </strong>
                        with the IRS. For vehicles first used in{' '}
                        <strong style={{ fontWeight: 600 }}>July 2026, </strong>
                        the filing deadline is{' '}
                        <strong style={{ fontWeight: 600 }}>
                          August 31, 2026.
                        </strong>
                      </span>
                    </li>

                    <li>
                      <span>
                        <Link
                          href="/resources/excise-tax/hvut-form-2290-penalties/"
                          sx={{ color: '#0202ff' }}
                        >
                          Penalties:
                        </Link>{' '}
                        Late filing may result in a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          4.5% penalty{' '}
                        </strong>
                        of the unpaid tax (increase up to five months). Failure{' '}
                        to pay the tax on time may result in an additional{' '}
                        <strong style={{ fontWeight: 600 }}>
                          0.5% monthly penalty,{' '}
                        </strong>
                        plus an interest of{' '}
                        <strong style={{ fontWeight: 600 }}>0.54% </strong>
                        until the balance is paid.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>IRS Form 2290</span>, also
                  known as the Heavy Highway Vehicle Use Tax Return, is a tax
                  form filed annually with the IRS by truckers and owners of
                  heavy vehicles weighing 55,000 pounds or more.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Here we will learn more about{' '}
                  <span style={{ fontWeight: 600 }}>HVUT Tax Form 2290</span>,
                  when it is due, and how and where to file it.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whatis"
                >
                  What is Form 2290?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link
                    // href={`${process.env.NEXT_PUBLIC_2290_SITE_URL}`}
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 2290
                  </Link>
                  , Heavy Highway Vehicle Use Tax Return, is used to figure and
                  pay road taxes for heavy highway motor vehicles with a{' '}
                  <span style={{ fontWeight: 600 }}>
                    <Link
                      href="/resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories/"
                      sx={{ color: '#0202ff' }}
                    >
                      taxable gross weight
                    </Link>{' '}
                    of 55,000 pounds or more
                  </span>{' '}
                  that operate on public highways.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  However, vehicles that are used for less than 5,000 miles
                  (7,500 miles for agricultural vehicles) during the tax period
                  may be exempt from the HVUT taxes but required to file Form
                  2290.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="whenis"
                >
                  When is the deadline for filing Form 2290?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline to{' '}
                  {/* <Link
                    href={`${process.env.NEXT_PUBLIC_2290_SITE_URL}`}
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  > */}
                  file Form 2290
                  {/* </Link>{' '} */} &nbsp;with the IRS will be the last day
                  of the month following the vehicle&apos;s First Used Month
                  (FUM). The current tax period for heavy highway vehicles
                  begins on July 1, 2026, and ends on June 30, 2027.
                </Typography>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',

                    border: '1px solid #ccc',
                    mb: 3,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    The annual Heavy Vehicle Use Tax Form 2290 filing deadline
                    for the tax year 2026{' '}
                    <Hidden smDown>
                      {' '}
                      <br />
                    </Hidden>{' '}
                    is <span style={{ fontWeight: 600 }}>August 31, 2026</span>.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290duedates`}
                  >
                    E-File Form 2290 Now
                  </Button>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Form 2290 Deadline for First Used Month Other than July
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  IRS Due Date Chart for Tax Year 2026-2027:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If, in this period,
                            <Hidden mdDown>
                              <br />
                            </Hidden>{' '}
                            the vehicle is first used during
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Then, file IRS Form 2290 and
                            <Hidden mdDown>
                              <br />
                            </Hidden>{' '}
                            make your payment by*
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>July 2026</TableCell>
                          <TableCell>August 31, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>August 2026</TableCell>
                          <TableCell>September 30, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>September 2026</TableCell>
                          <TableCell>November 2, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>October 2026</TableCell>
                          <TableCell>November 30, 2026</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>November 2026</TableCell>
                          <TableCell>December 31, 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>December 2026</TableCell>
                          <TableCell>February 1, 2027 </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>January 2027</TableCell>
                          <TableCell>March 1, 2027 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>February 2027</TableCell>
                          <TableCell>March 31, 2027</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>March 2027</TableCell>
                          <TableCell>April 30, 2027 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>April 2027</TableCell>
                          <TableCell>June 1, 2027 </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>May 2027</TableCell>
                          <TableCell>June 30, 2027</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>June 2027</TableCell>
                          <TableCell>August 2, 2027 </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="body2" sx={{ mt: 1, color: '#6f6c7f' }}>
                    *File by this date regardless of when the state registration
                    for the vehicle is due. If any due date falls on a Saturday,
                    Sunday, or legal holiday, file by the next business day.
                  </Typography>
                </Box>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'column' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          fontSize: '20px !important',
                        }}
                      >
                        E-file IRS Excise Tax Form 2290 with TaxZerone
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        File your annual HVUT Form 2290 with TaxZerone and get
                        your IRS Stamped Schedule 1 in minutes.
                      </Typography>
                      <Typography variant="body2">
                        Complete the entire filing process in 3 simple steps!
                      </Typography>
                    </Box>
                    <Box sx={{ width: '100%', textAlign: 'left' }}>
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            backgroundColor: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290duedates`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="howto"
                >
                  How to file Form 2290 for the 2026 tax year?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 2290 can be filed with the IRS either electronically or
                  through paper filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Electronic filing or E-filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing is the most convenient and efficient method to file
                  and transmit your HVUT Form to the IRS. The IRS even suggests
                  electronic filing since the process is quick and also provides
                  you with instant status updates of the filed return. You can
                  choose an e-file service provider authorized by the IRS to
                  file Form 2290.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  TaxZerone is an IRS-authorized e-file provider that supports
                  e-filing for{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 2290
                  </Link>
                  . You can complete the 2290 return with us and transmit it to
                  the IRS. We assist you in each step to make sure the e-filing
                  process goes smoothly.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Choose to e-file IRS Form 2290 with us and receive your
                  Stamped Schedule 1 within minutes!
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Refer to our{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 2290 instructions
                  </Link>{' '}
                  for step-by-step filing guidance.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Another method is by manually filling out the form and sending
                  the return to the IRS by postal mail.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you choose paper filing, make sure to send the form a
                  little earlier than the deadline, as it may take some time for
                  the return to reach the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  After filling out all the required information in the return,
                  you are required to manually send the return to the IRS by
                  postal mail.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Send a paper copy of Form 2290 to the mailing address
                  mentioned below:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If you are filing a paper return
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mailing Address
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            With full payment and that payment is not drawn from
                            an international financial institution.
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            P.O. Box 932500
                            <br />
                            Louisville, KY 40293-2500
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Without payment due or if payment is made through
                            EFTPS or by credit/debit card.
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            Internal Revenue Service
                            <br />
                            Ogden, UT 84201-0031
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            With a check or money order drawn from an
                            international financial institution.{' '}
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            International Accounts
                            <br />
                            1973 Rulon White Blvd.
                            <br />
                            Ogden, UT 84201-0038
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Although there are two filing methods available, the IRS
                  recommends electronic filing as the process is a simple,
                  quick, and efficient approach when compared to paper filing.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> To learn more about{' '}
                  <Link
                    href="/resources/excise-tax/hvut-form-2290-penalties/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 2290 penalties
                  </Link>
                  , refer to our page.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="penalty"
                >
                  Penalties for late filing Form 2290
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  While filing Form 2290, you must be aware of the penalty rate
                  for late filing of the return and failure to pay the tax dues
                  within the deadline. There are also penalties for filing false
                  or fraudulent returns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Below are the penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Failure to file the Heavy Vehicle Used Tax (HVUT)
                            return by the deadline.
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Failure to file both the 2290 return and heavy
                            vehicle taxes
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            4.5% of the total tax amount due and the penalty
                            will increase monthly for up to five months.
                          </TableCell>
                          <TableCell>
                            0.5% of your total tax amount. You will also face an
                            additional interest charge of 0.54% per month.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To avoid penalties, you can show a reasonable cause for
                  failing to file Form 2290 or pay taxes on time and can request
                  penalty relief by sending a letter to the IRS explaining the
                  reason.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 8, mt: { xs: 6, sm: 6, md: 6 } }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            textAlign: 'center',
          }}
        >
          First Used Month for Form 2290: <br className="hide-xs" />
          What You Need to Know
        </Typography>
        <Grid
          container
          spacing={2}
          m={0}
          mt={0}
          className="MuiGrid-root-width"
          sx={{ flexDirection: { xs: 'column', md: 'row' } }}
        >
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'center',
                xs: 'center',
              },
            }}
          >
            <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
              <Typography component="div" variant="body1" sx={{ mb: 3 }}>
                Ensure accurate reporting of the first used month on Form 2290
                and file your HVUT online using TaxZerone for a seamless IRS
                compliance.
              </Typography>

              <Box
                sx={{
                  width: 'auto',
                  textAlign: 'left',
                  alignItems: 'center',
                  display: 'inline-block',
                  mt: { xs: 2, sm: 2 },
                }}
              >
                <Button
                  variant="contained"
                  className="btn-primary medium"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290duedates`}
                  sx={{
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  Start E-Filing Now!
                </Button>
                {/* <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: 0, textAlign: 'center' }}
                  id="auto-magic-2290"
                >
                  Save time & Try it today!
                </Typography> */}
              </Box>
            </Box>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} textAlign="right">
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box
                component="iframe"
                title="What is Form 2290: A Comprehensive Guide"
                src="https://www.youtube.com/embed/jFmUNa9q4LM?&rel=0"
                sx={{
                  width: { xs: '100%', sm: 500 },
                  height: { xs: '100%', sm: 281 },
                  border: '10px solid #333 !important',
                  borderRadius: 5,
                }}
                allowFullScreen={true}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form2290DueDates;
